import React from 'react';
import { Link, useNavigate } from 'react-router';
import { BlogArticle } from 'types/interfaces/blog';
import './BlogPreview.css';

// TradeMagic News on the homepage. Design pass 5 (2026-09-29): "Read more
// articles" sits in the header row, right-aligned beside the title, styled like
// "View more" on Recent Activity, and always shows whatever the article count.
// It replaced the centered "Read More" MagicButton footer, which rendered
// unconditionally whenever the list was non-empty.

export const ARTICLES_TARGET = '/articles';

interface BlogPreviewProps {
  articles: BlogArticle[];
  isLoading: boolean;
}

const Header: React.FC = () => (
  <div className="blog-preview-header">
    <div className="blog-preview-header-row">
      <h3>TradeMagic News</h3>
      <Link to={ARTICLES_TARGET} className="blog-preview-more" data-testid="blog-preview-more">
        Read more articles
      </Link>
    </div>
    <p>Articles, updates, and strategy from the Magic community.</p>
  </div>
);

const BlogPreview: React.FC<BlogPreviewProps> = ({ articles, isLoading }) => {
  const navigate = useNavigate();

  const handleArticleClick = (slug: string) => {
    navigate(`/articles/${slug}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Show skeleton loading
  if (isLoading) {
    return (
      <div className="blog-preview">
        <Header />
        <div className="blog-preview-grid">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="blog-card-skeleton">
              <div className="skeleton-image" />
              <div className="skeleton-content">
                <div className="skeleton-title" />
                <div className="skeleton-subtitle" />
                <div className="skeleton-date" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Don't render if no articles (graceful degradation)
  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <div className="blog-preview">
      <Header />
      <div className="blog-preview-grid">
        {articles.map((article) => (
          <article
            key={article.id}
            className="blog-card"
            onClick={() => handleArticleClick(article.slug)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleArticleClick(article.slug);
              }
            }}
          >
            <div className="blog-card-image">
              {article.heroImageUrl ? (
                <img
                  src={article.heroImageUrl}
                  alt={article.title}
                  loading="lazy"
                />
              ) : (
                <div className="blog-card-placeholder" />
              )}
            </div>
            <div className="blog-card-content">
              <h4 className="blog-card-title">{article.title}</h4>
              {article.subtitle && (
                <p className="blog-card-subtitle">{article.subtitle}</p>
              )}
              {article.publishedAt && (
                <span className="blog-card-date">{formatDate(article.publishedAt)}</span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default BlogPreview;
